import React from 'react';
import { format } from 'date-fns';

import { Badge } from '@dashboard/components/ui/badge';
import getStripeAccountStatus, {
  STRIPE_ACCOUNT_STATUS_LABELS,
  type SellerWithStripeStatus,
  type StripeAccountStatus as Status,
} from '@dashboard/lib/stripe/get-stripe-account-status';

const VARIANTS: Record<Status, 'success' | 'warning' | 'destructive'> = {
  active: 'success',
  action_needed: 'warning',
  payouts_paused: 'warning',
  under_review: 'warning',
  restricted: 'destructive',
  rejected: 'destructive',
};

const formatDate = (value: string) => format(new Date(value), 'd MMM, yyyy');
const formatDateTime = (value: string) => format(new Date(value), 'd MMM, yyyy, HH:mm');

export default function StripeAccountStatus({ seller }: { seller: SellerWithStripeStatus }) {
  const stripe = seller?.paymentGateways?.stripe;
  if (!stripe?.isActive) return null;

  const status = getStripeAccountStatus(seller);
  if (!status) {
    return <div className="mt-1 mb-2 text-xs text-muted-foreground">Status not synced yet</div>;
  }

  const { disabledReason, currentDeadline, pastDue, currentlyDue, updatedAt } =
    stripe.accountStatus;

  const details = [
    disabledReason,
    currentDeadline && `Due by ${formatDate(currentDeadline)}`,
    pastDue?.length && `Past due: ${pastDue.join(', ')}`,
    currentlyDue?.length && `Currently due: ${currentlyDue.join(', ')}`,
    updatedAt && `Updated ${formatDateTime(updatedAt)}`,
  ].filter(Boolean);

  return (
    <div className="mt-1 mb-2 space-y-1">
      <Badge variant={VARIANTS[status]}>{STRIPE_ACCOUNT_STATUS_LABELS[status]}</Badge>
      {details.map(line => (
        <div key={line} className="text-xs text-muted-foreground break-words">
          {line}
        </div>
      ))}
    </div>
  );
}
